import { useState, useEffect, useCallback } from 'react';
import { Users, Plus, Edit2, Trash2, Save, X, RefreshCw, Shield, Briefcase, Eye, EyeOff, ArrowLeft, Key } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const UserManagementPage = ({ user }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [saving, setSaving] = useState(false);
  
  // View mode: 'list', 'create', 'edit', 'delete', 'reset-password'
  const [viewMode, setViewMode] = useState('list');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [formUsername, setFormUsername] = useState('');
  const [formName, setFormName] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formRole, setFormRole] = useState('employee');
  const [formIsActive, setFormIsActive] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  
  // Reset password form state
  const [resetPassword, setResetPassword] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);

  const getToken = useCallback(() => {
    const savedUser = localStorage.getItem('gb-sylt-user');
    if (savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        return parsed.token;
      } catch (e) {
        return null;
      }
    }
    return null;
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      const token = getToken();
      const response = await axios.get(`${API}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Load error:', error);
      setMessage({ type: 'error', text: 'Fehler beim Laden der Benutzer' });
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => setMessage({ type: '', text: '' }), 5000);
      return () => clearTimeout(timer);
    }
  }, [message.text]);

  const resetForm = () => {
    setFormUsername('');
    setFormName('');
    setFormPassword('');
    setFormRole('employee');
    setFormIsActive(true);
    setShowPassword(false);
    setResetPassword('');
    setShowResetPassword(false);
  };

  const goToList = () => {
    setViewMode('list');
    setSelectedUser(null);
    resetForm();
  };

  const goToCreate = () => {
    resetForm();
    setSelectedUser(null);
    setViewMode('create');
  };

  const goToEdit = (u) => {
    setFormUsername(u.username || '');
    setFormName(u.name || '');
    setFormPassword('');
    setFormRole(u.role || 'employee');
    setFormIsActive(u.is_active !== false);
    setShowPassword(false);
    setSelectedUser(u);
    setViewMode('edit');
  };

  const goToDelete = (u) => {
    setSelectedUser(u);
    setViewMode('delete');
  };

  const goToResetPassword = (u) => {
    setResetPassword('');
    setShowResetPassword(false);
    setSelectedUser(u);
    setViewMode('reset-password');
  };

  const handleSave = async () => {
    if (saving) return;

    if (!formUsername.trim() || !formName.trim()) {
      setMessage({ type: 'error', text: 'Benutzername und Name sind Pflichtfelder' });
      return;
    }

    if (viewMode === 'create' && !formPassword.trim()) {
      setMessage({ type: 'error', text: 'Passwort ist ein Pflichtfeld' });
      return;
    }

    setSaving(true);

    try {
      const token = getToken();

      if (viewMode === 'create') {
        await axios.post(`${API}/users`, {
          username: formUsername,
          password: formPassword,
          name: formName,
          role: formRole
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: `Benutzer "${formUsername}" erfolgreich erstellt` });
      } else if (viewMode === 'edit' && selectedUser) {
        const updateData = {
          username: formUsername,
          name: formName,
          role: formRole,
          is_active: formIsActive
        };
        if (formPassword && formPassword.trim()) {
          updateData.password = formPassword;
        }
        await axios.put(`${API}/users/${selectedUser.id}`, updateData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMessage({ type: 'success', text: `Benutzer "${formUsername}" erfolgreich aktualisiert` });
      }

      setSaving(false);
      setViewMode('list');
      setSelectedUser(null);
      resetForm();
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Speichern';
      setMessage({ type: 'error', text: errorMsg });
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedUser || saving) return;
    setSaving(true);

    try {
      const token = getToken();
      await axios.delete(`${API}/users/${selectedUser.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `Benutzer "${selectedUser.username}" erfolgreich gelöscht` });
      setSaving(false);
      setViewMode('list');
      setSelectedUser(null);
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Löschen';
      setMessage({ type: 'error', text: errorMsg });
      setSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser || saving) return;
    
    if (resetPassword.length < 6) {
      setMessage({ type: 'error', text: 'Passwort muss mindestens 6 Zeichen haben' });
      return;
    }
    
    setSaving(true);

    try {
      const token = getToken();
      await axios.post(`${API}/users/${selectedUser.id}/reset-password`, {
        new_password: resetPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage({ type: 'success', text: `Passwort für "${selectedUser.username}" zurückgesetzt. Benutzer muss beim nächsten Login ein neues Passwort setzen.` });
      setSaving(false);
      setViewMode('list');
      setSelectedUser(null);
      setResetPassword('');
      loadUsers();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Fehler beim Zurücksetzen des Passworts';
      setMessage({ type: 'error', text: errorMsg });
      setSaving(false);
    }
  };

  const isListView = viewMode === 'list';
  const isFormView = viewMode === 'create' || viewMode === 'edit';
  const isDeleteView = viewMode === 'delete';
  const isResetPasswordView = viewMode === 'reset-password';
  const isNewUser = viewMode === 'create';

  // Render everything in ONE consistent structure - use display:none to hide sections
  return (
    <div className="h-full overflow-y-auto bg-gray-100">
      <div className="max-w-4xl mx-auto p-4 lg:p-6 space-y-4">
        
        {/* ========== LIST VIEW ========== */}
        <div style={{ display: isListView ? 'block' : 'none' }}>
          {/* Header */}
          <div className="bg-white rounded-lg border p-4 flex items-center justify-between flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-lg bg-purple-600 flex items-center justify-center">
                <Users className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">Benutzerverwaltung</h1>
                <p className="text-sm text-gray-500">Benutzer anlegen, bearbeiten und löschen</p>
              </div>
            </div>
            <button
              type="button"
              onClick={goToCreate}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2 text-sm font-medium"
            >
              <Plus className="h-4 w-4" />
              Neuer Benutzer
            </button>
          </div>

          {/* Message Area */}
          <div className="mb-4" style={{ minHeight: '60px' }}>
            <div 
              className={`p-4 rounded-lg border flex items-center justify-between transition-opacity duration-200 ${
                message.type === 'error' ? 'bg-red-100 text-red-800 border-red-200' :
                message.type === 'success' ? 'bg-green-100 text-green-800 border-green-200' :
                'bg-blue-100 text-blue-800 border-blue-200'
              }`}
              style={{ display: message.text ? 'flex' : 'none' }}
            >
              <span>{message.text}</span>
              <button type="button" onClick={() => setMessage({ type: '', text: '' })} className="ml-4 p-1 hover:opacity-70">
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>

          {/* Info Box */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 text-sm text-purple-800 mb-4">
            <strong>Rollen:</strong>{' '}
            <span className="font-medium">Mitarbeiter</span> haben Zugriff auf Gast- und Mitarbeiter-Wissen.{' '}
            <span className="font-medium">Admins</span> haben zusätzlich Zugriff auf Admin-Wissen, Wissensverwaltung, Einstellungen und Benutzerverwaltung.
          </div>

          {/* Users List */}
          <div className="bg-white rounded-lg border overflow-hidden">
            <div style={{ display: loading ? 'block' : 'none' }} className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-gray-400 mb-2" />
              <p className="text-gray-500">Lade Benutzer...</p>
            </div>
            
            <div style={{ display: !loading && users.length === 0 ? 'block' : 'none' }} className="p-8 text-center">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p className="text-gray-500">Keine Benutzer vorhanden</p>
            </div>
            
            <div style={{ display: !loading && users.length > 0 ? 'block' : 'none' }} className="divide-y">
              {users.map((u, idx) => (
                <div 
                  key={`user-item-${u.id}-${idx}`}
                  className={`p-4 flex items-center justify-between gap-4 ${!u.is_active ? 'bg-gray-50' : ''}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`h-11 w-11 rounded-full flex items-center justify-center flex-shrink-0 ${
                      u.role === 'admin' ? 'bg-purple-600' : 'bg-blue-600'
                    }`}>
                      <span className="text-white font-medium">
                        {u.name?.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase() || '?'}
                      </span>
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-medium text-gray-900">{u.name}</p>
                        <span 
                          className="px-2 py-0.5 rounded text-xs bg-gray-200 text-gray-600"
                          style={{ display: !u.is_active ? 'inline' : 'none' }}
                        >
                          Deaktiviert
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-sm text-gray-500 font-mono">{u.username}</span>
                        <span 
                          className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                          style={{ 
                            backgroundColor: u.role === 'admin' ? '#f3e8ff' : '#dbeafe',
                            color: u.role === 'admin' ? '#7e22ce' : '#1d4ed8'
                          }}
                        >
                          {u.role === 'admin' ? <Shield className="h-3 w-3" /> : <Briefcase className="h-3 w-3" />}
                          {u.role === 'admin' ? 'Admin' : 'Mitarbeiter'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button
                      type="button"
                      onClick={() => goToResetPassword(u)}
                      className="p-2.5 text-gray-500 hover:text-orange-600 hover:bg-orange-50 rounded-lg"
                      title="Passwort zurücksetzen"
                    >
                      <Key className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => goToEdit(u)}
                      className="p-2.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg"
                      title="Bearbeiten"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => goToDelete(u)}
                      className="p-2.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg"
                      title="Löschen"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div 
            className="text-center text-sm text-gray-500 mt-4"
            style={{ display: !loading && users.length > 0 ? 'block' : 'none' }}
          >
            {users.length} Benutzer insgesamt · {users.filter(u => u.role === 'admin').length} Admins · {users.filter(u => u.role === 'employee').length} Mitarbeiter
          </div>
        </div>

        {/* ========== FORM VIEW (Create/Edit) ========== */}
        <div style={{ display: isFormView ? 'block' : 'none' }}>
          <button
            type="button"
            onClick={goToList}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Liste
          </button>

          <div className="bg-white rounded-lg border shadow-sm max-w-lg mx-auto">
            <div className="p-4 border-b">
              <h2 className="text-lg font-bold text-gray-900">
                {isNewUser ? 'Neuen Benutzer anlegen' : 'Benutzer bearbeiten'}
              </h2>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Benutzername *</label>
                <input
                  type="text"
                  value={formUsername}
                  onChange={(e) => setFormUsername(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="z.B. mmustermann"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vollständiger Name *</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  placeholder="Max Mustermann"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isNewUser ? 'Passwort *' : 'Neues Passwort (optional)'}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formPassword}
                    onChange={(e) => setFormPassword(e.target.value)}
                    className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                    placeholder={isNewUser ? 'Passwort eingeben' : 'Leer lassen um nicht zu ändern'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rolle *</label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setFormRole('employee')}
                    className="flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-2 transition-all"
                    style={{
                      backgroundColor: formRole === 'employee' ? '#2563eb' : 'transparent',
                      color: formRole === 'employee' ? 'white' : '#374151',
                      borderColor: formRole === 'employee' ? '#2563eb' : '#d1d5db'
                    }}
                  >
                    <Briefcase className="h-5 w-5" />
                    Mitarbeiter
                  </button>
                  <button
                    type="button"
                    onClick={() => setFormRole('admin')}
                    className="flex-1 py-3 px-4 rounded-lg border-2 text-sm font-medium flex items-center justify-center gap-2 transition-all"
                    style={{
                      backgroundColor: formRole === 'admin' ? '#9333ea' : 'transparent',
                      color: formRole === 'admin' ? 'white' : '#374151',
                      borderColor: formRole === 'admin' ? '#9333ea' : '#d1d5db'
                    }}
                  >
                    <Shield className="h-5 w-5" />
                    Admin
                  </button>
                </div>
              </div>

              <div 
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                style={{ display: !isNewUser ? 'flex' : 'none' }}
              >
                <input
                  type="checkbox"
                  id="form_is_active_checkbox"
                  checked={formIsActive}
                  onChange={(e) => setFormIsActive(e.target.checked)}
                  className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="form_is_active_checkbox" className="text-sm text-gray-700">
                  Benutzer ist aktiv (kann sich anmelden)
                </label>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={goToList}
                  disabled={saving}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                >
                  {saving ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
                  {saving ? 'Speichere...' : 'Speichern'}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ========== DELETE VIEW ========== */}
        <div style={{ display: isDeleteView ? 'block' : 'none' }}>
          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-6 text-center">
                <div className="mx-auto w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mb-4">
                  <Trash2 className="h-8 w-8 text-red-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Benutzer löschen?</h2>
                <p className="text-gray-600 mb-6">
                  Möchten Sie den Benutzer <strong>{selectedUser?.name || ''}</strong> ({selectedUser?.username || ''}) wirklich löschen?
                  <br />
                  <span className="text-sm text-red-600">Diese Aktion kann nicht rückgängig gemacht werden.</span>
                </p>
                
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={goToList}
                    disabled={saving}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={saving}
                    className="flex-1 py-3 px-4 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                  >
                    {saving ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Trash2 className="h-5 w-5" />}
                    {saving ? 'Lösche...' : 'Endgültig löschen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ========== RESET PASSWORD VIEW ========== */}
        <div style={{ display: isResetPasswordView ? 'block' : 'none' }}>
          <button
            type="button"
            onClick={goToList}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Zurück zur Liste
          </button>

          <div className="max-w-lg mx-auto">
            <div className="bg-white rounded-lg border shadow-sm">
              <div className="p-4 border-b">
                <h2 className="text-lg font-bold text-gray-900">Passwort zurücksetzen</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Setzen Sie ein neues temporäres Passwort für <strong>{selectedUser?.name}</strong> ({selectedUser?.username})
                </p>
              </div>

              <div className="p-4 space-y-4">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 text-sm text-orange-800">
                  <strong>Hinweis:</strong> Der Benutzer wird beim nächsten Login aufgefordert, ein eigenes Passwort zu wählen.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Neues temporäres Passwort *</label>
                  <div className="relative">
                    <input
                      type={showResetPassword ? 'text' : 'password'}
                      value={resetPassword}
                      onChange={(e) => setResetPassword(e.target.value)}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                      placeholder="Mindestens 8 Zeichen"
                    />
                    <button
                      type="button"
                      onClick={() => setShowResetPassword(!showResetPassword)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-500 hover:text-gray-700"
                    >
                      {showResetPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Mindestens 6 Zeichen erforderlich</p>
                </div>

                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={goToList}
                    disabled={saving}
                    className="flex-1 py-3 px-4 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium disabled:opacity-50"
                  >
                    Abbrechen
                  </button>
                  <button
                    type="button"
                    onClick={handleResetPassword}
                    disabled={saving || resetPassword.length < 6}
                    className="flex-1 py-3 px-4 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                  >
                    {saving ? <RefreshCw className="h-5 w-5 animate-spin" /> : <Key className="h-5 w-5" />}
                    {saving ? 'Wird gespeichert...' : 'Passwort zurücksetzen'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};
